/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons/faBookOpen';
import { faCopy } from '@fortawesome/free-solid-svg-icons/faCopy';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { Action, Store } from '@ngrx/store';
import { InterventionSharedService } from '../../../services/shared/intervention-shared/intervention-shared.service';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { getCollabInterventionById } from '../../../store/intervention/intervention.selector';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { InterventionActionTypes } from 'src/app/store/intervention/intervention.action';
import { RoleInterface } from 'src/app/models/interface/role.interface';
import { environment } from 'src/environments/environment';
import { StudyStore } from 'src/app/store/study/component-store/study.store';
import { StudyInterface } from 'src/app/models/interface/study/study.interface';
import { Actions, ofType } from '@ngrx/effects';
import { GetOrganisationCollaboratorsSuccess, OrganisationActionTypes } from 'src/app/store/organisation/organisation.action';
import { OrganisationCollaboratorInterface } from 'src/app/models/interface/organisation/organisation-collaborator.interface';
import { HttpResponse } from '@angular/common/http';
import { GroupSharedService } from 'src/app/services/shared/group-shared/group-shared.service';
import { UserStore } from 'src/app/store/user/component-store/user.store';
import { UserSharedService } from 'src/app/services/shared/user-shared/user-shared.service';

type Tab = 'lessons' | 'skills' | 'collaborators' | 'instances' | 'tasks' | 'activities' | 'diaries';

/**
 * Component:
 * Intervention details page displaying an intervention;
 * Can be found: {uri}/interventions/{{intervention_id}}/
 */
@Component({
  selector: 'app-intervention-detail',
  templateUrl: './intervention-detail.component.html',
  styleUrls: ['./intervention-detail.component.scss'],
  providers: [DiaryStore, InterventionSharedService, StudyStore, UserStore],
})
export class InterventionDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('matTabGroup') matTabGroup: QueryList<MatTabGroup>;

  // Icons
  faBookOpen = faBookOpen;
  faCopy = faCopy;
  faUsers = faUsers;
  faHands = faHands;
  faHandsHelping = faHandsHelping;
  faTrash = faTrash;
  faPlus = faPlus;

  private patientInterventionUrl = environment.patientURL + '/lesson-overview/';

  public isLoadingSubject = new BehaviorSubject<boolean>(true);

  public intervention: InterventionInterface = null;
  public intervention$: Observable<InterventionInterface | undefined>;

  public diaries: DiaryInterface[] = [];

  public profile: ProfileInterface;

  public isECoach$: Observable<boolean>;
  public isManager$: Observable<boolean>;
  public isPublisherOrAccess$: Observable<boolean>;
  public isPublisher$: Observable<boolean>;
  public isInterventionActive$: Observable<boolean>;

  public profile$: Observable<ProfileInterface>;

  public diariesOfStudy$: Observable<DiaryInterface[]>;

  private translationSubject = new BehaviorSubject<any>({});

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;
  private allCollaborators$: Observable<UserInterface[]>;
  private inactiveCollabInterventions$: Observable<InterventionInterface[]>;

  private isECoachStudySubject = new BehaviorSubject<boolean>(false);
  private isManagerSubject = new BehaviorSubject<boolean>(false);
  private isPublisherOrAccessSubject = new BehaviorSubject<boolean>(false);
  private isPublisherSubject = new BehaviorSubject<boolean>(false);
  private isInterventionActiveSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];
  private studyDetail$: Observable<StudyInterface>;

  private study: StudyInterface;

  constructor(
    private actRoute: ActivatedRoute,
    private router: Router,
    private actions$: Actions,
    private diaryStore: DiaryStore,
    private studyStore: StudyStore,
    private userStore: UserStore,
    private sharedService: InterventionSharedService,
    private translateService: TranslateService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private store: Store<{
      myProfile: ProfileInterface;
      getCollabInterventionById: InterventionInterface;
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
      inactiveCollabInterventions: InterventionInterface[];
    }>,
    private groupSharedService: GroupSharedService,
    private userSharedService: UserSharedService,
  ) {
    this.isECoach$ = this.isECoachStudySubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isPublisherOrAccess$ = this.isPublisherOrAccessSubject.asObservable();
    this.isPublisher$ = this.isPublisherSubject.asObservable();
    this.isInterventionActive$ = this.isInterventionActiveSubject.asObservable();
    this.studyDetail$ = this.studyStore.studyDetail$ as Observable<StudyInterface>;
    this.profile$ = store.select('myProfile');
    this.inactiveCollabInterventions$ = this.store.select('inactiveCollabInterventions');
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.allCollaborators$ = this.userStore.allCollaborators$;

    this.subscriptions.push(
      this.translateService
        .getTranslation(localStorage.getItem('language') !== null ? localStorage.getItem('language') : 'en')
        .subscribe((translations) => this.translationSubject.next(translations)),
    );
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.getInterventionContent(true);

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        this.translationSubject.next(event.translations);
        this.getInterventionContent();
      })
    );
  }

  ngAfterViewInit(): void {
    this.matTabGroup.changes.subscribe((_comps: QueryList<MatTabGroup>) => {
      this.helper.setMatTabActive(
        this.matTabGroup,
        this.router.url,
        this.translationSubject,
        this.translateService,
        'intervention-detail',
      );
    });
  }

  // Display and navigate to child component relative to this path
  public showChildComponent(child: Tab | ''): void {
    if (child !== '') {
      this.router.navigate([child], { relativeTo: this.actRoute });
    }
  }

  public updateActivatedChild(child: any) {
    if (child.study !== undefined) {
      child.study = this.study;
    }
  }

  // Listens to tab change
  public onChangeTab(event: MatTabChangeEvent): void {
    const tab: MatTab = event.tab;
    const child = this.evaluateTab(tab.textLabel, this.translationSubject.value);
    this.showChildComponent(child);
  }

  public getCatalogue(): void {
    this.router.navigateByUrl('/interventions');
  }

  // Get study name
  public getDetailedStudy(studyId: number): void {
    this.router.navigateByUrl(`/groups/${studyId}`);
  }

  copyInterventionUrlToClipboard(interventionId: number): void {
    const url = this.patientInterventionUrl + interventionId;
    navigator.clipboard.writeText(url);
  }

  public openDialogInterventionDelete(intervention: InterventionInterface): void {
    this.helperDialog
      .openDialogInterventionDelete(intervention)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'SUCCESS') {
          this.router.navigateByUrl('/interventions');
        }
      });
  }

  public copiedInterventionBySameUser(intervention: InterventionInterface, userId: number): boolean {
    return (!intervention.attributes.copied_by_user || !userId)
      || intervention.attributes.copied_by_user.toString() === userId.toString();
  }

  public openDialogInterventionTestPublish(intervention: InterventionInterface): void {
    this.helperDialog
      .openDialogInterventionTestPublish(intervention)
      .afterClosed()
      .subscribe((_result) => {
        this.getInterventionContent();
      });
  }

  public openDialogInterventionOfficialPublish(intervention: InterventionInterface): void {
    this.helperDialog
      .openDialogInterventionOfficialPublish(intervention)
      .afterClosed()
      .subscribe((_result) => {
        this.getInterventionContent();
      });
  }

  private evaluateTab(label: string, translate: any): Tab | '' {
    if (translate) {
      if (translate['intervention-detail']['lessons'] === label) {
        return 'lessons';
      }
      if (translate['intervention-detail']['skills'] === label) {
        return 'skills';
      }
      if (translate['intervention-detail']['collaborators'] === label) {
        return 'collaborators';
      }
      if (translate['intervention-detail']['instances'] === label) {
        return 'instances';
      }
      if (translate['intervention-detail']['tasks'] === label) {
        return 'tasks';
      }
      if (translate['intervention-detail']['activities'] === label) {
        return 'activities';
      }
      if (translate['intervention-detail']['diaries'] === label) {
        return 'diaries';
      }
    }
    return '';
  }

  private getOrganisationCollaborators(complete?: boolean) {
    if (!complete) {
      return of(null);
    }
    this.studyStore.getStudyDetail({ studyId: this.intervention.attributes.study_id });
    return this.studyDetail$.pipe(
      skip(1),
      take(1),
      mergeMap((study) => {
        this.study = study;
        const organisationId = parseInt(this.study.attributes.organisation_id.toString(), 10);
        this.store.dispatch({
          type: OrganisationActionTypes.getOrganisationCollaboratorsType,
          payload: { organisationId },
        });
        return this.actions$.pipe(
          ofType(
            OrganisationActionTypes.getOrganisationCollaboratorsSuccessType,
            OrganisationActionTypes.getOrganisationCollaboratorsErrorType,
          ),
          take(1),
          mergeMap((action: Action) => of(action)),
          catchError(() => of(null)),
        );
      }),
    );
  }

  private getDiariesUnlockedByIntervention(intervention: InterventionInterface): Observable<DiaryInterface[]> {
    if (Array.isArray(intervention.attributes.unlock_diaries) && intervention.attributes.unlock_diaries.length > 0) {
      this.diaryStore.getDiariesOfStudy({ studyId: intervention.attributes.study_id });
      return this.diariesOfStudy$.pipe(skip(1), take(1));
    } else {
      return of([]);
    }
  }

  private getStudyCollaborators(complete?: boolean): Observable<UserInterface[] | null> {
    if (!complete) {
      return of(null);
    }
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratorsType,
      payload: { studyId: this.intervention.attributes.study_id, include: 'roles' },
    });
    this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.intervention.attributes.study_id));
    return this.collaboratorsOfStudy$.pipe(
      filter((collaboratorsOfStudy) => !!collaboratorsOfStudy),
      map((collaboratorsOfStudy) => collaboratorsOfStudy.collaborators),
      take(1),
    );
  }

  // Get and display detailed study details
  private getInterventionContent(complete?: boolean): void {
    this.isLoadingSubject.next(true);
    let interventionId: number;
    this.subscriptions.push(
      this.profile$
        .pipe(
          filter((user) => user !== null),
          take(1),
          mergeMap((value: ProfileInterface) => {
            this.profile = value;
            return this.actRoute.params;
          }),
          map((v) => parseInt(v.id, 10)),
          switchMap((id: number) => {
            interventionId = id;
            this.store.dispatch({
              type: InterventionActionTypes.getInterventionsOfCollabStudyType,
              payload: { typeOfParentStudy: 'study' },
            });
            return this.inactiveCollabInterventions$.pipe(skip(1), take(1));
          }),
          switchMap((inactiveInterventions: InterventionInterface[]) => {
            const foundInactiveIntervention = inactiveInterventions.find(
              (intervention) => intervention.id.toString() === interventionId.toString(),
            );
            if (foundInactiveIntervention !== undefined) {
              return of(foundInactiveIntervention);
            }
            this.intervention$ = this.store.select(getCollabInterventionById(interventionId));
            return this.intervention$.pipe(
              filter((intervention: InterventionInterface | undefined) => !!intervention),
              take(1),
            );
          }),
          mergeMap((intervention: InterventionInterface) => {
            this.intervention = intervention;
            if (this.intervention.attributes.is_active === 1) {
              this.isInterventionActiveSubject.next(true);
            }
            const organisationCollaborators = this.getOrganisationCollaborators(complete);
            const diaries = this.getDiariesUnlockedByIntervention(this.intervention);
            const studyCollaborators = this.getStudyCollaborators(complete);
            this.userStore.getAllCollaboratorsOfUser({ roles: 'ecoach' });
            const allCollaboratos = this.allCollaborators$.pipe(
              skip(1),
              take(1),
              mergeMap((result) => {
                return of(result);
              }),
              catchError(() => {
                return of<UserInterface[]>([]);
              }),
            );
            return forkJoin({ allCollaboratos, organisationCollaborators, diaries, studyCollaborators });
          }),
        )
        .subscribe({
          next: (result) => {
            if (result.organisationCollaborators?.type === OrganisationActionTypes.getOrganisationCollaboratorsSuccessType) {
              const resp: HttpResponse<any> = (result.organisationCollaborators as GetOrganisationCollaboratorsSuccess).response;
              const collaborators: OrganisationCollaboratorInterface[] = resp.body?.data || null;
              const profileId = this.profile.id.toString();
              const myself = collaborators?.find((user) => user.id.toString() === profileId);
              if (myself) {
                const myRole = this.helperService.getHighestRoleOfOrganisationCollaborator(myself);
                if (myRole === 'organisation.manager' || myRole === 'organisation.owner') {
                  this.groupSharedService.pushIsOrganisationManager(true);
                }
              }
            }
            this.userSharedService.pushCollaborators(result.allCollaboratos);
            this.diaries = result.diaries;
            // Determine my highest role
            const myself = result.studyCollaborators?.find(
              (user) => user.id.toString() === this.profile.id.toString(),
            );
            if (myself !== undefined) {
              const myRole = this.helper.getHighestRoleOfCollaborator(myself);
              const isManager = myRole === 'study.ecoachmanager' || myRole === 'study.owner';
              const isECoach = isManager || myRole === 'study.ecoach';
              this.isECoachStudySubject.next(isECoach);
              this.sharedService.pushIsECoach(isECoach);
              this.isManagerSubject.next(isManager);
              const isPublisherOrAccess = myRole === 'study.publisher' || myRole === 'study.access';
              this.isPublisherOrAccessSubject.next(isPublisherOrAccess);
              this.isPublisherSubject.next(
                myself.relationships.roles.data.find(
                  (value: RoleInterface) => value.attributes.slug === 'study.publisher',
                ),
              );
            }
            this.sharedService.pushIntervention(this.intervention);
            this.isLoadingSubject.next(false);
          },
          error: (error) => {
            throwError(() => error);
            this.router.navigateByUrl('/interventions');
          },
          complete: () => {
            this.isLoadingSubject.next(false);
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
